import React from 'react';

import { DeleteOutlined } from '@ant-design/icons';
import { Button, InputNumber, Select, Typography, Space, Dropdown } from 'antd';

import { METRICS } from '../../constants/data';
import { ConditionAtom, Comparator, MetricKey } from '../../types';
import { metricByKey } from '../../utils/helpers';

interface AtomRowProps {
  atom: ConditionAtom;
  onChange: (a: ConditionAtom) => void;
  onRemove?: () => void;
}

const COMPARATORS: Array<{ value: Comparator; label: string }> = [
  { value: '>', label: 'Больше' },
  { value: '<', label: 'Меньше' },
];

function getDayDeclension(days: number): string {
  const lastDigit = days % 10;
  const lastTwoDigits = days % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
    return 'дней';
  }

  if (lastDigit === 1) {
    return 'день';
  }

  if (lastDigit >= 2 && lastDigit <= 4) {
    return 'дня';
  }

  return 'дней';
}

function formatDatePeriod(periodDays: number, periodDaysUntil: number): string {
  const today = new Date();
  const startDate = new Date(today);

  startDate.setDate(today.getDate() - periodDays);

  let endDate;
  if (periodDaysUntil === 0) {
    // Для режима "последние" - от N дней назад до сегодня
    endDate = new Date(today);
  } else {
    // Для режима "период" - от periodDays до periodDaysUntil дней назад
    endDate = new Date(today);
    endDate.setDate(today.getDate() - periodDaysUntil);
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
}

export function AtomRow({ atom, onChange, onRemove }: AtomRowProps) {
  const m = metricByKey(atom.metric)!;
  const datePeriod = formatDatePeriod(atom.periodDays ?? 0, atom.periodDaysUntil ?? 0);
  const [selectedPeriodType, setSelectedPeriodType] = React.useState<string>('последние');

  // Синхронизируем тип периода с приходящими данными (редактирование)
  React.useEffect(() => {
    const isPeriod = (atom.periodDaysUntil ?? 0) > 0;
    setSelectedPeriodType(isPeriod ? 'период' : 'последние');
  }, [atom.periodDaysUntil]);

  // Определяем, нужно ли показывать единицы измерения
  const shouldShowUnits = () => {
    // Метрики без единиц измерения
    const noUnitsMetrics = [
      'users_count', 'ads_clicks_count', 'ads_impressions', 'web_users_count'
    ];

    return !noUnitsMetrics.includes(atom.metric);
  };

  // Определяем единицу измерения по умолчанию для метрики
  const getDefaultUnit = () => {
    // Метрики в процентах
    const percentMetrics = [
      'new_users_part', 'ctr', 'ads_click_to_reg_converse', 'romi', 'drr',
      'web_5min_users_part', 'web_15min_users_part', 'web_60min_g_users_part', 'web_120min_g_users_part',
      'user_to_web_user_converse', 'user_to_buyer_converse'
    ];

    if (percentMetrics.includes(atom.metric)) {
      return 'percent';
    }

    // Все остальные метрики в рублях
    return 'rub';
  };

  const periodMenuItems = selectedPeriodType === 'последние'
    ? [{ key: 'period', label: 'период' }]
    : [{ key: 'last', label: 'последние' }];

  const handlePeriodMenuClick = ({ key }: { key: string }) => {
    if (key === 'last') {
      setSelectedPeriodType('последние');
      // При выборе "последние" устанавливаем periodDaysUntil в 0
      onChange({ ...atom, periodDaysUntil: 0 });
    } else if (key === 'period') {
      setSelectedPeriodType('период');
    }
  };

  return (
    <div style={{
      border: '1px solid #d9d9d9',
      background: '#fafafa',
      borderRadius: 10,
      padding: 12,
      position: 'relative',
    }}>
      {/* Delete button inside condition block */}
      {onRemove && (
        <div style={{
          position: 'absolute',
          top: 4,
          right: 4,
          zIndex: 1
        }}>
          <Button
            type="text"
            icon={<DeleteOutlined />}
            onClick={onRemove}
            size="small"
            style={{ color: 'rgba(0, 0, 0, 0.88)' }}
          />
        </div>
      )}

      <div style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        paddingRight: onRemove ? 40 : 0, // Add padding to avoid overlap with buttons
      }}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, width: '100%' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Typography.Text type="secondary" style={{ whiteSpace: 'nowrap' }}>За </Typography.Text>
              <Dropdown
                menu={{
                  items: periodMenuItems,
                  onClick: handlePeriodMenuClick,
                }}
                trigger={['click']}
              >
                <Typography.Text
                  style={{
                    color: '#1677ff',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {selectedPeriodType}
                </Typography.Text>
              </Dropdown>
              {selectedPeriodType === 'период' && (
                <Typography.Text type="secondary" style={{ whiteSpace: 'nowrap' }}> с</Typography.Text>
              )}
              <InputNumber
                min={0}
                value={atom.periodDays ?? 0}
                onChange={(v) => onChange({ ...atom, periodDays: Number(v ?? 0) })}
                style={{ width: 50 }}
              />
              {selectedPeriodType === 'период' && (
                <>
                  <Typography.Text type="secondary" style={{ whiteSpace: 'nowrap' }}>по</Typography.Text>
                  <InputNumber
                    min={0}
                    value={atom.periodDaysUntil ?? 0}
                    onChange={(v) => onChange({ ...atom, periodDaysUntil: Number(v ?? 0) })}
                    style={{ width: 50 }}
                  />
                  <Typography.Text type="secondary" style={{ whiteSpace: 'nowrap' }}>дней назад</Typography.Text>
                </>
              )}
              {selectedPeriodType === 'последние' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Typography.Text type="secondary" style={{ whiteSpace: 'nowrap' }}>{getDayDeclension(atom.periodDays ?? 0)}</Typography.Text>
                </div>
              )}
            </div>
            {selectedPeriodType === 'последние' && (
              <Typography.Text type="secondary" style={{ fontSize: 12, color: '#8c8c8c' }}>
                ({datePeriod})
              </Typography.Text>
            )}
            {selectedPeriodType === 'период' && (
              <Typography.Text type="secondary" style={{ fontSize: 12, color: '#8c8c8c' }}>
                {datePeriod}
              </Typography.Text>
            )}
          </div>

          <Select
            value={atom.metric}
            style={{ width: 320 }}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            onChange={(v) => {
              const next = v as MetricKey;
              const def = METRICS.find((x) => x.key === next)?.defaultComparator || atom.comparator;
              onChange({ ...atom, metric: next, comparator: def });
            }}
          >
            {METRICS.map((m) => (
              <Select.Option key={m.key} value={m.key}>
                {m.label}
              </Select.Option>
            ))}
          </Select>

          <Select
            value={atom.comparator}
            style={{ width: 140, textAlign: 'center' }}
            onChange={(v) => onChange({ ...atom, comparator: v as Comparator })}
          >
            {COMPARATORS.map((c) => (
              <Select.Option key={c.value} value={c.value}>
                {c.label}
              </Select.Option>
            ))}
          </Select>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <InputNumber
              placeholder={m.placeholder}
              value={atom.value === '' ? undefined : (atom.value as number)}
              onChange={(val) => onChange({ ...atom, value: val === null || val === undefined ? '' : Number(val) })}
              style={{ width: 70 }}
              formatter={(value) => {
                if (!value) return '';
                // Форматируем число с разделением тысячных
                return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
              }}
              parser={(value) => {
                if (!value) return 0;
                // Убираем пробелы и заменяем запятую на точку
                return Number(value.replace(/\s/g, '').replace(',', '.'));
              }}
              decimalSeparator=","
              precision={2}
              step={0.01}
            />
            {shouldShowUnits() && (
              <Typography.Text type="secondary">
                {getDefaultUnit() === 'percent' ? '%' : 'руб.'}
              </Typography.Text>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
